import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
// import axiosRetry, {
//   exponentialDelay,
//   isNetworkOrIdempotentRequestError,
// } from 'axios-retry';
import qs from 'qs';
import { authService } from './services/auth-service';
import { getSentryInstance } from './sentry';
import { getEnv } from './utils';

export interface AuthResponse {
  token_type: string;
  user_id: string;
  access_token: string;
  expires_in: number;
  target?: Record<string, unknown>;
}

type Urls = Record<string, string>;

// Axios attaches a custom marker so a refreshed request is only retried once.
type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

const URLS: Urls = {
  base: getEnv(import.meta.env.VITE_APP_API_BASE),
  authenticate: '/authenticate',
  signIn: '/signin',
  signOut: '/signout',
  user: '/user',
  presets: '/presets',
  orders: '/orders',
  trades: '/trades',
  tenders: '/tenders',
  divisions: '/divisions',
  products: '/products',
  customers: '/customers',
  marketOrders: '/market-orders',
  marketTrades: '/market-trades',
  marketDepth: '/market-depth',
  fcmToken: '/register',
  oauthRefresh: '/token',
  oauthRevoke: '/user/revoke',
  acceptPolicy: '/user/accept-policy',
  userActivity: '/user/activity',
  userMobile: '/user/mobile',
  userVerifySMS: '/user/verify-sms',
  userConfirmSMS: '/user/confirm-sms',
  participants: '/participants',
  solutions: '/solutions',
  routes: '/routes',
  analytics: getEnv(import.meta.env.VITE_APP_ANALYTICS_URL),
};

// Taken from CreamApp
export class API {
  token: AuthResponse | null = null;
  onSessionExpired: (() => void) | null = null;
  _refreshPromise: Promise<AuthResponse> | null = null;
  urls: Urls;
  ax: AxiosInstance;

  constructor(urls: Urls = URLS) {
    this.urls = urls;
    this.ax = axios.create({
      baseURL: urls.base,
      // TODO Enable this when components are robust
      // timeout: 10000,
      headers: {
        // axios having issue of defaults headers, before we use axios@next
        // 'Content-Type': 'application/json'
      },
      withCredentials: true,
    });
    this.ax.defaults.headers['Content-Type'] = 'application/json';
    this.ax.interceptors.request.use(
      (req: AxiosRequestConfig) => {
        // axios has issue of defaults headers, before we use axios@next
        req.headers ??= {};
        if (this.token) {
          req.headers.Authorization = `${this.token.token_type} ${this.token.access_token}`;
        }
        const replayId = getSentryInstance()?.getReplay()?.getReplayId();
        if (replayId) {
          req.headers['X-Sentry-Replay-Id'] = replayId;
        }
        return req;
      },
      (e) => Promise.reject(e)
    );
    this.ax.interceptors.response.use(
      (resp) => resp,
      (error: AxiosError) => this._handle401(error)
    );
  }

  // Endpoints excluded from the 401 refresh-and-retry flow to avoid loops.
  // refresh() uses raw axios so it already bypasses this.ax.
  _shouldSkipRefresh = (url?: string) => {
    if (!url) return true;
    const pathname = url.split('?')[0];
    return [
      this.urls.signIn,
      this.urls.signOut,
      this.urls.authenticate,
      this.urls.oauthRefresh,
    ].some((p) => p && pathname.endsWith(p));
  };

  _handle401 = async (error: AxiosError) => {
    const { response } = error;
    const config = error.config as RetryConfig | undefined;
    if (
      !response ||
      response.status !== 401 ||
      !config ||
      config._retried ||
      this._shouldSkipRefresh(config.url)
    ) {
      return Promise.reject(error);
    }

    this._refreshPromise ??= this.refresh().finally(() => {
      this._refreshPromise = null;
    });

    try {
      const token = await this._refreshPromise;
      authService.saveToken(token);
      this.updateToken(token);
      config._retried = true;
      config.headers ??= {};
      config.headers.Authorization = `${token.token_type} ${token.access_token}`;
      return this.ax.request(config);
    } catch {
      this.onSessionExpired?.();
      return Promise.reject(error);
    }
  };

  // After signin, set token here, when signout clear token
  // In between, use the token to refresh, if refresh success, signal to update oauthToken, else reject w/ error;
  // listener to store directly would cause circular importing, thus manually set here
  updateToken = (token: AuthResponse | null) => {
    this.token = token;
  };

  // user, trades, orders, divisions, products, etc
  getData = (
    {
      type,
      id,
      division,
      skin,
      getResult = (x: any) => Object.values(x)[0] || [],
    }: {
      type: string;
      id?: string;
      division?: unknown;
      skin?: string;
      getResult?: (x: any) => any;
    },
    extraParams?: Record<string, unknown>,
    extra?: AxiosRequestConfig
  ) => {
    const prefix = this.urls[type];
    const baseUrl = id ? `${prefix}/${id}` : prefix;
    // Prepend skin to URL if provided (e.g., /hilmar/orders)
    const url = skin ? `/${skin}${baseUrl}` : baseUrl;
    const params = division ? { division } : null;
    return (
      this.ax
        .request({
          url,
          params: { ...params, ...extraParams },
          ...extra,
        })
        // XXX When empty, {} is returned instead of such as { orders: [] }
        .then((resp) => getResult(resp.data))
    );
  };

  getData2 = (
    url: string,
    params: Record<string, unknown> = {},
    { skin }: { skin?: string } = {}
  ) =>
    this.ax
      .request({
        url: skin ? `/${skin}/${url.replace(/^\/+/, '')}` : url,
        params,
        method: 'get',
      })
      .then((resp) => resp.data);

  getDataV2Api = (url: string, params: Record<string, unknown> = {}) =>
    this.ax
      .request({
        // baseURL: 'https://dev.cream.global/api/2.0',
        baseURL: this.urls.base.replace('/api/1.0', '/api/2.0'),
        url,
        params,
        method: 'get',
      })
      .then((resp) => resp.data);

  deleteData = (url: string, params: Record<string, unknown> = {}) =>
    this.ax
      .request({
        url,
        params,
        method: 'delete',
      })
      .then((resp) => resp.data);

  putData = (url: string, data: any) =>
    this.ax
      .request({
        url,
        data,
        method: 'put',
      })
      .then((resp) => resp.data);

  patchData = (url: string, data: any) =>
    this.ax
      .request({
        url,
        data,
        method: 'patch',
      })
      .then((resp) => resp.data);

  postData = (url: string, data: any) =>
    this.ax
      .request({
        url,
        data,
        method: 'post',
      })
      .then((resp) => resp.data);

  getDataIfModifiedSince = (
    url: string,
    params: Record<string, unknown>,
    ifModifiedSinceDate?: Date
  ): Promise<[number, any]> => {
    const config: AxiosRequestConfig = {
      url,
      params,
      paramsSerializer: (params) => qs.stringify(params, { indices: false }),
    };
    if (ifModifiedSinceDate) {
      const date = new Date(ifModifiedSinceDate.getTime() - 120000);
      config.headers = {
        'If-Modified-Since': date.toUTCString(),
      };
      config.validateStatus = (status) => {
        return status === 200 || status === 304;
      };
    }

    return this.ax
      .request(config)
      .then((resp): [number, any] => [resp.status, resp.data])
      .catch((error): [number, any] => {
        console.error('getDataIfModifiedSince error:', error);
        return [error.response?.status || 500, null];
      });
  };

  get request() {
    return this.ax.request;
  }

  requestAnalytics({
    endpoint,
    solutionId,
    args = null,
  }: {
    endpoint: string;
    solutionId: string;
    args?: string[] | null;
  }) {
    const prefix = this.urls['analytics'];
    let url = `${prefix}/${endpoint}/${solutionId}`;
    if (args) {
      url += '/' + args.join('/');
    }
    return this.ax.request({ url }).then((resp) => {
      return resp;
    });
  }

  analytics(
    endpoint: string,
    args: string[] = [],
    params: Record<string, unknown> = {}
  ) {
    let url = [this.urls.analytics, endpoint].concat(args).join('/');
    if (params) {
      // useQuerystring is a legacy qs option not present in @types/qs.
      url +=
        '?' +
        qs.stringify(params, {
          indices: false,
          useQuerystring: true,
        } as qs.IStringifyOptions);
    }
    return this.ax.get(url).then((resp) => resp);
  }

  // redirectTo: deep-link path to preserve across magic-link auth (backend
  // stores it on the token's `target`, echoed back from /authenticate).
  signIn = (email: string, solution?: string, redirectTo?: string) =>
    this.ax.post(this.urls.signIn, {
      email,
      solution,
      ...(redirectTo && { redirect_to: redirectTo }),
    });

  signOut = () => this.ax.post(this.urls.signOut);

  authenticate = (
    token: string,
    client: string,
    email?: string
  ): Promise<AuthResponse> =>
    this.ax
      .request({
        method: 'post',
        url: this.urls.authenticate,
        // email is required to resolve a non-unique 5-digit short code;
        // magic-link (full token, no email) falls back to token-only lookup
        data: { token, client, ...(email && { email }) },
      })
      .then((resp) => resp.data);

  getMarketDepth = (params: Record<string, unknown>) =>
    this.ax
      .request({ url: this.urls.marketDepth, params })
      .then((resp) => resp.data.data);

  sendMobileVerifySMS = (number: string) =>
    this.ax
      .request({
        method: 'post',
        url: this.urls.userVerifySMS,
        data: { number },
      })
      .then((resp) => resp.data);

  checkMobileVerifyToken = (token: string) =>
    this.ax
      .request({
        method: 'post',
        url: this.urls.userConfirmSMS,
        data: { token },
      })
      .then((resp) => resp.data);

  fok = (data: any) => this.ax.post(this.urls.orders, data);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  revoke = (token?: string) => this.ax.post(this.urls.oauthRevoke);

  acceptPolicy = (solution: string) =>
    this.ax.post(this.urls.acceptPolicy, { solution });
  userActivity = (data: any) =>
    this.ax.post(this.urls.userActivity, data, {
      validateStatus: (status) => status === 200 || status === 403,
    });

  getPdf = (url: string, filename: string) =>
    this.ax
      .request({
        url: url.replace('/api/1.0', ''),
        method: 'get',
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      })
      .then((response) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      });

  getFile = (url: string, filename: string, mimeType: string) =>
    this.ax
      .request({
        url: url.replace('/api/1.0', ''),
        method: 'get',
        responseType: 'blob',
        headers: {
          Accept: mimeType,
        },
      })
      .then((response) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      });

  getFileHref = (url: string, mimeType: string) =>
    this.ax
      .request({
        url: url.replace('/api/1.0', ''),
        method: 'get',
        responseType: 'blob',
        headers: {
          Accept: mimeType,
        },
      })
      .then((response) =>
        window.URL.createObjectURL(new Blob([response.data]))
      );

  // TODO
  refresh = (): Promise<AuthResponse> => {
    const client_id = getEnv(import.meta.env.VITE_APP_WEB_CLIENT_ID);
    return axios
      .request({
        url: this.urls.oauthRefresh,
        method: 'POST',
        data: qs.stringify({
          client: client_id,
        }),
        baseURL: this.urls.base,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        withCredentials: true,
      })
      .then((resp) => {
        const { token_type, user_id, access_token, expires_in } = resp.data;
        return {
          user_id,
          access_token,
          token_type,
          expires_in,
        };
      })
      .catch((error) => {
        // Enhance error information for better debugging
        if (error.response?.status === 401) {
          throw Object.assign(
            new Error('No valid session found - please sign in'),
            {
              status: 401,
              isAuthError: true,
            }
          );
        }
        throw error;
      });
  };

  // registerFcmToken = address => {
  //   return this.ax.post(this.urls.fcmToken, { address });
  // };
}

// TODO From env
export const api = new API();
